/**
 * Format a number as a whole-dollar price string, e.g. `948.95` → `"$948"`.
 *
 * Rounds DOWN (truncates) rather than to nearest, so a displayed "from"/offer
 * price never reads higher than the real one, and it matches the legacy site.
 * Every product-page price figure (sticky nav, offer cards, level tabs, model
 * comparison, scorecard, price alert…) goes through this so rounding stays
 * consistent across surfaces.
 */
export const formatPrice = (price: number): string => `$${Math.floor(price).toLocaleString('en-US')}`

/** A `price_override` decomposed into the parts the product card renders. */
export interface ParsedPriceOverride {
  /** The string led with "From" — render the translated `price.from` prefix. */
  from: boolean
  /** Display amount, already `$`-formatted. May be a range (`"$4,500 – $7,000"`). */
  amount: string
  /** Trailing unit, if the string named one — render the translated suffix. */
  unit: 'pair' | 'ear' | null
}

const FROM_RE = /^from\b\s*/i
const PAIR_RE = /\s*(?:\/|per)\s*pair\s*$/i
const EAR_RE = /\s*(?:\/|per)\s*ear\s*$/i
const AMOUNT_RE = /\$\s?\d[\d,]*(?:\.\d{1,2})?/g
const BARE_NUMBER_RE = /^\d[\d,]*(?:\.\d{1,2})?$/

/** `"1,199"` → `"$1,199"`, `"29.95"` → `"$29.95"`. Cents are kept when authored:
 *  these are accessory prices, where rounding 19.99 to $20 overstates. */
const formatBareAmount = (value: string): string => {
  const num = Number(value.replace(/,/g, ''))
  if (!Number.isFinite(num)) {
    return value
  }
  const fractionDigits = Number.isInteger(num) ? 0 : 2
  return `$${num.toLocaleString('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}`
}

/**
 * Decompose a Storyblok `price_override` into `From` / amount / unit so the card
 * can render each part with its own translated chrome — authors write English
 * ("From $3,998 / pair"), and printing that verbatim would leak English into de.
 *
 * An override that can't be decomposed (a phrase, a stray unit) is returned
 * verbatim with no chrome, so a "From" or "/ pair" already in the string can
 * never be printed twice — a bug the old app shipped for `"From 1,199 / ear"`.
 */
export const parsePriceOverride = (value: string | number): ParsedPriceOverride => {
  const raw = String(value).trim()
  if (!raw) {
    return { from: false, amount: '', unit: null }
  }

  const unit = PAIR_RE.test(raw) ? 'pair' : EAR_RE.test(raw) ? 'ear' : null
  const from = FROM_RE.test(raw)
  const core = raw.replace(FROM_RE, '').replace(PAIR_RE, '').replace(EAR_RE, '').trim()

  // A range ("$4,500 to $7,000") keeps both bounds; the old app dropped the upper one.
  const amounts = core.match(AMOUNT_RE)?.map((amount) => amount.replace(/\$\s+/, '$'))
  if (amounts && amounts.length > 1) {
    return { from, amount: `${amounts[0]} – ${amounts.at(-1)}`, unit }
  }
  if (BARE_NUMBER_RE.test(core)) {
    return { from, amount: formatBareAmount(core), unit }
  }
  if (amounts?.length === 1 && amounts[0] === core) {
    return { from, amount: amounts[0], unit }
  }
  // Core still holds text we didn't consume — an unmarked upper bound
  // ("$6,000 to 8,000+"), say. Keep it whole rather than matching the first
  // amount and silently understating the price, but keep the translated chrome:
  // `core` has already had the "From" and unit stripped, so nothing double-prints.
  if (/\d/.test(core)) {
    return { from, amount: core, unit }
  }
  return { from: false, amount: raw, unit: null }
}
